import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import { useNow } from '@vueuse/core';
import * as Sentry from '@sentry/vue';
import { useAuthStore } from '@/composition/use-auth';
import { useStableWindowSize } from '@/composition/use-stable-window-size';
import {
  assistantDot,
  TAssistantPendingProposals,
} from '@/domain/assistant/assistant-dot';
import {
  assistantInsetRight,
  assistantTier,
  TAssistantSheetHeight,
} from '@/domain/assistant/assistant-panel-tier';
import { isHelpBubbleShown } from '@/domain/assistant/assistant-rollout';
import { gpthelpChatbotId } from '@/gpthelp/gpthelp-loader';
import { clearGpthelpStorage } from '@/gpthelp/gpthelp-storage';
import { isGpthelpSupportedBrowser } from '@/gpthelp/gpthelp-support';
import type {
  IAssistantActivityItem,
  IAssistantConfigResponse,
} from '@/interfaces/assistant.interface';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { assistantEnabledFor } from './use-assistant-rollout';
import { useGpthelpScope } from './use-gpthelp-scope';

/**
 * Loading state of the widget inside the panel: `unsupported` = the browser
 * cannot run it (the script is never requested).
 */
export type TAssistantStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unsupported';

/** What opened the panel (Sentry breadcrumb). */
export type TAssistantOpenSource = 'pill' | 'shortcut' | 'coachmark' | 'host';

/** An applied or undone assistant action, echoed in the host context. */
export interface IAssistantRecent {
  actionId: string;
  status: IAssistantActivityItem['status'];
  title: string;
  /** ISO instant of the apply or undo. */
  at: string;
  /** `r{rid}-b{bid}` of the store and branch it happened in. */
  scopeKey: string;
}

/** A request to flash product rows on the Menú page (plan §9.10). */
export interface IAssistantHighlight {
  itemIds: number[];
  categoryId: number | null;
  /** Grows on every request, so the same rows can be highlighted again. */
  token: number;
  /** `Date.now()` of the request: the Menú page ignores a stale one. */
  requestedAt: number;
}

/** The host context carries at most this many recent actions (per store and branch). */
export const ASSISTANT_RECENT_MAX = 3;

/** The pending dot re-checks proposal expiry this often (ms). */
const NOW_INTERVAL_MS = 30_000;

/**
 * State of the «Asistente» panel (plan §9.2). The panel components own the
 * DOM; the host adapter, tools and card actions (plan §9.5–§9.9) talk to the
 * panel only through the actions below.
 */
export const useAssistantStore = defineStore('assistant', () => {
  const restaurantStore = useRestaurantStore();
  const authStore = useAuthStore();
  const { width: stableWidth } = useStableWindowSize();
  const now = useNow({ interval: NOW_INTERVAL_MS });
  /** The browser can run the GPTHelp widget (it never changes at runtime). */
  const widgetSupported = isGpthelpSupportedBrowser();

  const open = ref(false);
  /** The first navigation resolved: the route guard has loaded the store. */
  const routeReady = ref(false);
  /** False on routes without the panel (POS workspaces, chromeless flows). */
  const routeAllowed = ref(false);
  /** A POS workspace: neither the panel nor the bubble shows. */
  const posRoute = ref(false);
  const status = ref<TAssistantStatus>('idle');
  const sheetHeight = ref<TAssistantSheetHeight>('lg');
  /** True while a turn runs (the widget's `busy-change`). */
  const busy = ref(false);
  /** The widget's protocol version (its `ready` event); null until it is ready. */
  const protocolVersion = ref<number | null>(null);
  const pendingProposals = shallowRef<TAssistantPendingProposals>({});
  const recent = shallowRef<IAssistantRecent[]>([]);
  /** `GET assistant/config` of `configKey`'s store and branch (fetched by the host adapter). */
  const config = shallowRef<IAssistantConfigResponse | null>(null);
  const configKey = ref<string | null>(null);
  /** `Date.now()` when `config` was stored; 0 = ask again on the next `ensureAssistantConfig`. */
  const configLoadedAt = ref(0);
  const highlight = shallowRef<IAssistantHighlight | null>(null);
  let highlightSeq = 0;

  const tier = computed(() => assistantTier(stableWidth.value));
  /**
   * The rollout gives this user and store the panel instead of the bubble
   * (never outside a store, never to guests).
   */
  const enabled = computed(
    () =>
      !!authStore.user &&
      assistantEnabledFor(
        restaurantStore.restaurant?.stringId ?? null,
        !!authStore.user.admin
      )
  );
  const hasScope = computed(
    () =>
      typeof restaurantStore.restaurant?.id === 'number' &&
      typeof restaurantStore.selectedBranch?.id === 'number'
  );
  /**
   * The current store and branch (undefined outside a store): the pending
   * proposals and recent actions of another one never show here.
   */
  const { scopeKey } = useGpthelpScope();
  /**
   * The pill shows and the panel may open. Never on a browser that cannot run
   * the widget (old POS WebViews): like the bubble, nothing shows there.
   */
  const available = computed(
    () =>
      enabled.value && hasScope.value && routeAllowed.value && widgetSupported
  );
  const shown = computed(() => open.value && available.value);
  /**
   * V2AssistantRoot renders the GPTHelp bubble (plan §9.14): on every page
   * without the panel, never on POS workspaces nor on browsers that cannot
   * run it. The drawer's room for the bubble at its end follows it.
   */
  const bubbleShown = computed(() =>
    isHelpBubbleShown({
      routeReady: routeReady.value,
      supported: widgetSupported,
      panelEnabled: enabled.value,
      posRoute: posRoute.value,
    })
  );
  const insetRight = computed(() =>
    assistantInsetRight({ shown: shown.value, tier: tier.value })
  );
  const dot = computed(() =>
    assistantDot(
      busy.value,
      pendingProposals.value,
      now.value.getTime(),
      scopeKey.value ?? null
    )
  );

  const actions = {
    openPanel(source: TAssistantOpenSource) {
      if (!available.value || open.value) return;
      sheetHeight.value = 'lg';
      open.value = true;
      Sentry.addBreadcrumb({
        category: 'assistant',
        message: 'open',
        data: { source },
      });
    },
    closePanel() {
      open.value = false;
    },
    toggle(source: TAssistantOpenSource) {
      if (open.value) actions.closePanel();
      else actions.openPanel(source);
    },
    /**
     * After a navigation from the panel («Ver en el menú»): the overlay
     * closes, the sheet drops to its low height, the docked panel stays.
     */
    revealPage() {
      if (tier.value === 'overlay') actions.closePanel();
      else if (tier.value === 'sheet') sheetHeight.value = 'sm';
    },
    setSheetHeight(height: TAssistantSheetHeight) {
      sheetHeight.value = height;
    },
    setRouteReady() {
      routeReady.value = true;
    },
    setRouteAllowed(allowed: boolean) {
      routeAllowed.value = allowed;
    },
    setPosRoute(pos: boolean) {
      posRoute.value = pos;
    },
    setStatus(value: TAssistantStatus) {
      status.value = value;
    },
    setBusy(value: boolean) {
      busy.value = value;
    },
    setProtocolVersion(version: number | null) {
      protocolVersion.value = version;
    },
    setConfig(key: string | null, value: IAssistantConfigResponse | null) {
      configKey.value = key;
      config.value = value;
      configLoadedAt.value = Date.now();
    },
    /**
     * The switches changed since the config was read (an
     * `ASSISTANT_WRITES_OFF` answer): it stays in use, and is asked again.
     */
    expireConfig() {
      configLoadedAt.value = 0;
    },
    /**
     * A proposal of `scopeKey` (its store and branch) waits for the user's
     * OK; it replaces the one it superseded.
     */
    markPending(
      id: string,
      rows: number,
      expiresAt: string,
      supersededId: string | null,
      scopeKey: string
    ) {
      const next = { ...pendingProposals.value };
      if (supersededId) delete next[supersededId];
      next[id] = { rows, expiresAt, scopeKey };
      pendingProposals.value = next;
    },
    /** Applied, discarded, expired, superseded or other-scope: no longer pending. */
    clearPending(id: string) {
      if (!(id in pendingProposals.value)) return;
      const next = { ...pendingProposals.value };
      delete next[id];
      pendingProposals.value = next;
    },
    /**
     * Newest first, at most `ASSISTANT_RECENT_MAX` per store and branch; an
     * action seen again (e.g. undone) replaces its older entry.
     */
    addRecent(entry: IAssistantRecent) {
      const others = recent.value.filter(
        (item) => item.actionId !== entry.actionId
      );
      recent.value = [
        entry,
        ...others
          .filter((item) => item.scopeKey === entry.scopeKey)
          .slice(0, ASSISTANT_RECENT_MAX - 1),
        ...others.filter((item) => item.scopeKey !== entry.scopeKey),
      ];
    },
    requestHighlight(itemIds: number[], categoryId: number | null) {
      highlightSeq += 1;
      highlight.value = {
        itemIds: [...itemIds],
        categoryId,
        token: highlightSeq,
        requestedAt: Date.now(),
      };
    },
    /** The Menú page took the request `token` (a newer request stays). */
    clearHighlight(token: number) {
      if (highlight.value?.token === token) highlight.value = null;
    },
    /** Another user logged in or the user logged out: nothing of the previous identity survives. */
    resetForIdentity() {
      open.value = false;
      busy.value = false;
      pendingProposals.value = {};
      recent.value = [];
      configKey.value = null;
      config.value = null;
      configLoadedAt.value = 0;
      highlight.value = null;
      clearGpthelpStorage(gpthelpChatbotId());
    },
  };

  // Watched here, in the store's own scope (plan §9.11): logout unmounts the
  // panel (it needs a user) first, and the watchers of that subtree are torn
  // down before they would see the user leave.
  watch(
    () => authStore.user?.id ?? null,
    (id, previous) => {
      if (previous !== null && id !== previous) actions.resetForIdentity();
    }
  );

  return {
    open,
    routeReady,
    routeAllowed,
    posRoute,
    status,
    sheetHeight,
    busy,
    protocolVersion,
    pendingProposals,
    recent,
    config,
    configKey,
    configLoadedAt,
    highlight,
    tier,
    enabled,
    available,
    shown,
    bubbleShown,
    insetRight,
    dot,
    ...actions,
  };
});
